/**
 * useUOM — fetches unit-of-measure options from the API once per app load.
 *
 * Returns:
 *   uomOptions  — [{ value: symbol, label: symbol }]  (for <Select> dropdowns)
 *   uomFull     — [{ id, symbol, name, category }]     (full objects if needed)
 *   uomLoading  — bool
 *
 * Uses a module-level cache so the API is called exactly once regardless of
 * how many components use this hook simultaneously.
 */

import { useState, useEffect } from 'react';
import { getUOMOptions } from '../api';

let _cache = null;          // resolved data
let _promise = null;        // in-flight request
const _listeners = new Set(); // components waiting for the result

function notifyAll(data) {
  _cache = data;
  _listeners.forEach(fn => fn(data));
  _listeners.clear();
}

export function useUOM() {
  const [uomFull, setUomFull] = useState(_cache || []);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (_cache) {
      setUomFull(_cache);
      setLoading(false);
      return;
    }

    const listener = (data) => {
      setUomFull(data);
      setLoading(false);
    };
    _listeners.add(listener);

    if (!_promise) {
      _promise = getUOMOptions()
        .then(data => { notifyAll(data); })
        .catch(() => { notifyAll([]); _promise = null; });
    }

    return () => { _listeners.delete(listener); };
  }, []);

  const uomOptions = uomFull.map(u => ({ value: u.symbol, label: u.symbol }));

  return { uomOptions, uomFull, uomLoading: loading };
}

/** Clears the module cache — useful after admin adds/edits a UOM entry. */
export function invalidateUOMCache() {
  _cache = null;
  _promise = null;
}
